/**
 * Regions of the object form and their distribution system operators (ОСР).
 * D2 (owner, 21.10.2026): all 24 oblasts and the city of Kyiv — not Crimea
 * or Sevastopol; no «Інша область». The DSO goes by the region. `value` is
 * what `sites.region` stores (the Ukrainian name), the operator's key —
 * `sites.osr_code`. A confirmed network rate exists only for the operators
 * with `hasRate` (their rates: `apps/api/src/config/vpp.ts`, a test keeps the
 * two in step); for the others the potential is «немає ставки ОСР», while
 * telemetry and internal arbitrage work.
 */
export const DSO_OPERATOR_LIST = {
    vinnytsia: { uk: 'Вінницяобленерго', en: 'Vinnytsiaoblenergo', hasRate: false },
    volyn: { uk: 'Волиньобленерго', en: 'Volynoblenergo', hasRate: false },
    'dtek-dnem': { uk: 'ДТЕК Дніпровські електромережі', en: 'DTEK Dnipro Grids', hasRate: true },
    cek: { uk: 'ЦЕК', en: 'CEC', hasRate: true },
    'dtek-dem': { uk: 'ДТЕК Донецькі електромережі', en: 'DTEK Donetsk Grids', hasRate: false },
    zhytomyr: { uk: 'Житомиробленерго', en: 'Zhytomyroblenergo', hasRate: false },
    zakarpattia: { uk: 'Закарпаттяобленерго', en: 'Zakarpattiaoblenergo', hasRate: false },
    zaporizhzhia: { uk: 'Запоріжжяобленерго', en: 'Zaporizhzhiaoblenergo', hasRate: false },
    prykarpattia: { uk: 'Прикарпаттяобленерго', en: 'Prykarpattyaoblenergo', hasRate: true },
    'dtek-kem': { uk: 'ДТЕК Київські електромережі', en: 'DTEK Kyiv Grids', hasRate: true },
    'dtek-krem': {
        uk: 'ДТЕК Київські регіональні електромережі',
        en: 'DTEK Kyiv Regional Grids',
        hasRate: true,
    },
    kirovohrad: { uk: 'Кіровоградобленерго', en: 'Kirovohradoblenergo', hasRate: false },
    leo: { uk: "Луганське енергетичне об'єднання", en: 'Luhansk Energy Association', hasRate: false },
    lviv: { uk: 'Львівобленерго', en: 'Lvivoblenergo', hasRate: true },
    mykolaiv: { uk: 'Миколаївобленерго', en: 'Mykolaivoblenergo', hasRate: false },
    'dtek-oem': { uk: 'ДТЕК Одеські електромережі', en: 'DTEK Odesa Grids', hasRate: false },
    poltava: { uk: 'Полтаваобленерго', en: 'Poltavaoblenergo', hasRate: false },
    rivne: { uk: 'Рівнеобленерго', en: 'Rivneoblenergo', hasRate: false },
    sumy: { uk: 'Сумиобленерго', en: 'Sumyoblenergo', hasRate: false },
    ternopil: { uk: 'Тернопільобленерго', en: 'Ternopiloblenergo', hasRate: false },
    kharkiv: { uk: 'Харківобленерго', en: 'Kharkivoblenergo', hasRate: false },
    kherson: { uk: 'Херсонобленерго', en: 'Khersonoblenergo', hasRate: false },
    khmelnytskyi: { uk: 'Хмельницькобленерго', en: 'Khmelnytskoblenergo', hasRate: false },
    cherkasy: { uk: 'Черкасиобленерго', en: 'Cherkasyoblenergo', hasRate: true },
    chernivtsi: { uk: 'Чернівціобленерго', en: 'Chernivtsioblenergo', hasRate: false },
    chernihiv: { uk: 'Чернігівобленерго', en: 'Chernihivoblenergo', hasRate: false },
};
/** In Ukrainian alphabetical order. */
export const REGION_LIST = [
    { value: 'Вінницька', uk: 'Вінницька', en: 'Vinnytsia region', osr: ['vinnytsia'] },
    { value: 'Волинська', uk: 'Волинська', en: 'Volyn region', osr: ['volyn'] },
    {
        value: 'Дніпропетровська',
        uk: 'Дніпропетровська',
        en: 'Dnipropetrovsk region',
        osr: ['dtek-dnem', 'cek'],
    },
    { value: 'Донецька', uk: 'Донецька', en: 'Donetsk region', osr: ['dtek-dem'] },
    { value: 'Житомирська', uk: 'Житомирська', en: 'Zhytomyr region', osr: ['zhytomyr'] },
    { value: 'Закарпатська', uk: 'Закарпатська', en: 'Zakarpattia region', osr: ['zakarpattia'] },
    { value: 'Запорізька', uk: 'Запорізька', en: 'Zaporizhzhia region', osr: ['zaporizhzhia'] },
    {
        value: 'Івано-Франківська',
        uk: 'Івано-Франківська',
        en: 'Ivano-Frankivsk region',
        osr: ['prykarpattia'],
    },
    { value: 'Київ', uk: 'м. Київ', en: 'Kyiv city', osr: ['dtek-kem'] },
    { value: 'Київська', uk: 'Київська', en: 'Kyiv region', osr: ['dtek-krem'] },
    { value: 'Кіровоградська', uk: 'Кіровоградська', en: 'Kirovohrad region', osr: ['kirovohrad'] },
    { value: 'Луганська', uk: 'Луганська', en: 'Luhansk region', osr: ['leo'] },
    { value: 'Львівська', uk: 'Львівська', en: 'Lviv region', osr: ['lviv'] },
    { value: 'Миколаївська', uk: 'Миколаївська', en: 'Mykolaiv region', osr: ['mykolaiv'] },
    { value: 'Одеська', uk: 'Одеська', en: 'Odesa region', osr: ['dtek-oem'] },
    { value: 'Полтавська', uk: 'Полтавська', en: 'Poltava region', osr: ['poltava'] },
    { value: 'Рівненська', uk: 'Рівненська', en: 'Rivne region', osr: ['rivne'] },
    { value: 'Сумська', uk: 'Сумська', en: 'Sumy region', osr: ['sumy'] },
    { value: 'Тернопільська', uk: 'Тернопільська', en: 'Ternopil region', osr: ['ternopil'] },
    { value: 'Харківська', uk: 'Харківська', en: 'Kharkiv region', osr: ['kharkiv'] },
    { value: 'Херсонська', uk: 'Херсонська', en: 'Kherson region', osr: ['kherson'] },
    { value: 'Хмельницька', uk: 'Хмельницька', en: 'Khmelnytskyi region', osr: ['khmelnytskyi'] },
    { value: 'Черкаська', uk: 'Черкаська', en: 'Cherkasy region', osr: ['cherkasy'] },
    { value: 'Чернівецька', uk: 'Чернівецька', en: 'Chernivtsi region', osr: ['chernivtsi'] },
    { value: 'Чернігівська', uk: 'Чернігівська', en: 'Chernihiv region', osr: ['chernihiv'] },
];
//# sourceMappingURL=regions.js.map